import { useMemo } from 'react';
import { useApolloClient } from '@apollo/client';
import { Item as ChequeItem } from '@baemingo/table-events/dist/types';
import { useStorage } from '~Components/Storage';
import { sortByCourseTypeOrder } from '~Utils/sortArticles';
import { combineArticles } from '~Utils/combineArticles';
import { groupOfferMatches } from '~Utils/offers';
import { useI18n, useSelector } from '~Hooks';

export type OrderedChequeItem = ChequeItem & { count?: number };

/**
 * Orders cheque items exactly like the cheque views do (Tables Articles on iPad,
 * ChequeDetails on Handheld): sorted by course type, combined when the cart is
 * compressed, unmatched items first and offer groups after.
 */
const useOrderedChequeItems = (items: ChequeItem[]) => {
  const client = useApolloClient();
  const { i18n } = useI18n();
  const [storage] = useStorage();
  const venueId = useSelector((state) => state.venueId);

  return useMemo(() => {
    const sortedItems = sortByCourseTypeOrder(items, client) as OrderedChequeItem[];

    const grouped = venueId
      ? groupOfferMatches(sortedItems, venueId, client, i18n)
      : { matches: [], unmatched: sortedItems };

    const itemsArr: OrderedChequeItem[] = storage.compressedCart
      ? combineArticles(sortedItems)
      : sortedItems;

    /** Same lookup as the cheque views, needed because of combineArticles */
    const unmatched = grouped.unmatched
      .map((unmatchedItem) => itemsArr.find((i) => i.id === unmatchedItem.id))
      .filter((item): item is OrderedChequeItem => Boolean(item));

    const matches = grouped.matches.map((offerMatch) => ({
      offerId: offerMatch.offerId,
      items: offerMatch.items,
      oldPrice: offerMatch.items.reduce((acc, item) => acc + item.amount, 0),
      newPrice: offerMatch.items.reduce(
        (acc, item) => acc + item.amount - (item.discount ?? 0),
        0,
      ),
    }));

    return { unmatched, matches };
  }, [items, client, venueId, i18n, storage.compressedCart]);
};

export default useOrderedChequeItems;
